import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useShoppingCart } from '../hooks/useShoppingCart'
import { useTheme } from '../hooks/useTheme'
import { toastError, toastSuccess } from '../utils/toastHelper'
import { formatCurrency } from '../utils/currencyHelpers'
import SimpleHeader from '../components/common/SimpleHeader'
import Footer from '../components/common/Footer'
import LoadingSpinner from '../components/common/LoadingSpinner'

interface ClientFormData {
  nombre: string
  email: string
  telefono: string
  numero_documento: string
  tipo_documento: string
  solicitudes_especiales: string
}

const ShoppingCartPage: React.FC = () => {
  const navigate = useNavigate()
  const { colors } = useTheme()
  const { cart, removeItem, clearCart, setBloqueo } = useShoppingCart()

  const [submitting, setSubmitting] = useState(false)

  const [formData, setFormData] = useState<ClientFormData>({
    nombre: '',
    email: '',
    telefono: '',
    numero_documento: '',
    tipo_documento: 'RUT',
    solicitudes_especiales: ''
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleRemoveItem = (espacio_id: string) => {
    removeItem(espacio_id)
    toastSuccess('Espacio removido del carrito')
  }

  const handleContinueShopping = () => {
    navigate('/')
  }

  const handleProceedToPayment = async () => {
    // Validaciones
    if (cart.items.length === 0) {
      toastError('El carrito está vacío')
      return
    }

    if (!formData.nombre || !formData.email || !formData.telefono || !formData.numero_documento) {
      toastError('Por favor completa todos los datos del cliente')
      return
    }

    setSubmitting(true)

    try {
      // 1. Crear bloqueos (iniciar-reserva)
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/espacios/iniciar-reserva`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            espacios: cart.items.map(item => ({
              espacio_id: item.espacio_id,
              fecha_entrada: item.fecha_entrada,
              fecha_salida: item.fecha_salida
            })),
            multiples: true,
            cantidad_huespedes: cart.items.length
          })
        }
      )

      if (!response.ok) {
        throw new Error('Error al crear bloqueo de reserva')
      }

      const result = await response.json()
      const bookingData = result.data || result

      if (!bookingData.booking_token) {
        throw new Error('No se recibió token de bloqueo')
      }

      // 2. Actualizar carrito con token y estado
      setBloqueo(bookingData.booking_token, bookingData.expires_at)

      // 3. Crear resumen simplificado del carrito para CheckoutPage
      const cartSummary = cart.items.map(item => {
        // Truncar nombre a máx 50 caracteres
        const nombreTruncado = item.nombre.length > 50 ? item.nombre.substring(0, 47) + '...' : item.nombre
        return {
          nombre: nombreTruncado,
          fecha_entrada: item.fecha_entrada,
          fecha_salida: item.fecha_salida,
          monto_total: item.monto_total
        }
      })

      // 4. Navegar a CheckoutPage con los datos del cliente y carrito
      navigate('/checkout', {
        state: {
          bookingData,
          clientData: formData,
          cartItems: cartSummary
        }
      })

      toastSuccess('Iniciando proceso de pago...')
    } catch (err) {
      toastError(err instanceof Error ? err.message : 'Error al proceder al pago')
    } finally {
      setSubmitting(false)
    }
  }

  // Si carrito está vacío
  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <SimpleHeader
          title="Carrito de Compras"
          subtitle="Tu carrito está vacío"
        />

        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Carrito Vacío
            </h3>
            <p className="text-gray-600 text-lg max-w-md mx-auto mb-8">
              Agrega espacios a tu carrito para comenzar tu reserva
            </p>
            <button
              onClick={handleContinueShopping}
              className="px-8 py-4 text-lg rounded-xl font-semibold text-white transition-all"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primaryDark
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary
              }}
            >
              Continuar Buscando
            </button>
          </div>
        </div>

        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SimpleHeader
        title="Carrito de Compras"
        subtitle={`${cart.cantidad_espacios} espacio${cart.cantidad_espacios !== 1 ? 's' : ''} seleccionado${cart.cantidad_espacios !== 1 ? 's' : ''}`}
      />

      <div className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* COLUMNA IZQUIERDA: Items + Datos del cliente + Método de pago */}
          <div className="lg:col-span-2 space-y-8">
            {/* SECCIÓN 1: Listado de items */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Espacios en el Carrito
                </h2>
                <button
                  onClick={() => {
                    clearCart()
                    toastSuccess('Carrito vaciado')
                  }}
                  className="text-sm font-semibold text-red-500 hover:text-red-700 transition-colors"
                >
                  Vaciar Carrito
                </button>
              </div>

              <div className="space-y-4">
                {cart.items.map((item) => (
                  <div key={item.espacio_id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-gray-900">{item.nombre}</h3>
                        <p className="text-sm text-gray-600">{item.codigo}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          {item.fecha_entrada} → {item.fecha_salida}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveItem(item.espacio_id)}
                        className="text-red-500 hover:text-red-700 font-bold text-xl transition-colors"
                        title="Remover del carrito"
                      >
                        ✕
                      </button>
                    </div>

                    {/* Desglose de tarifa por día */}
                    {item.desglose && item.desglose.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Desglose por {item.unidad_reserva}:</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                          {item.desglose.map((dia: any) => (
                            <div key={dia.fecha} className="text-xs bg-gray-50 rounded p-2">
                              <span className="text-gray-600">{dia.fecha}:</span>
                              <span className="font-semibold text-gray-900 ml-1">${(dia.tarifa || 0).toLocaleString('es-CL')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Total del item */}
                    <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                      <span className="text-sm font-semibold text-gray-900">Total:</span>
                      <span className="font-bold text-lg" style={{ color: colors.primary }}>
                        {formatCurrency(item.monto_total)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SECCIÓN 2: Datos del cliente */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Datos del Cliente
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                    style={{ focusRingColor: colors.primary }}
                    placeholder="Tu nombre completo"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                    style={{ focusRingColor: colors.primary }}
                    placeholder="tu@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                    style={{ focusRingColor: colors.primary }}
                    placeholder="+56 9 .... ...."
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Documento *
                    </label>
                    <select
                      name="tipo_documento"
                      value={formData.tipo_documento}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                      style={{ focusRingColor: colors.primary }}
                      required
                    >
                      <option value="RUT">RUT</option>
                      <option value="Pasaporte">Pasaporte</option>
                      <option value="Cédula">Cédula</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número de Documento *
                    </label>
                    <input
                      type="text"
                      name="numero_documento"
                      value={formData.numero_documento}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                      style={{ focusRingColor: colors.primary }}
                      placeholder="12345678-9"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Solicitudes Especiales
                  </label>
                  <textarea
                    name="solicitudes_especiales"
                    value={formData.solicitudes_especiales}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all resize-none"
                    style={{ focusRingColor: colors.primary }}
                    placeholder="Ej: Llegada tardía, habitación con vista al lago, etc."
                  />
                </div>
              </div>
            </div>

          </div>

          {/* COLUMNA DERECHA: Resumen */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Resumen</h3>

              <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
                {cart.items.map((item) => (
                  <div key={item.espacio_id} className="flex justify-between text-sm">
                    <span className="text-gray-600">{item.nombre}</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(item.monto_total)}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center mb-6">
                <span className="text-lg font-bold text-gray-900">Total:</span>
                <span
                  className="text-2xl font-bold"
                  style={{ color: colors.primary }}
                >
                  {formatCurrency(cart.monto_total)}
                </span>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleContinueShopping}
                  className="w-full px-4 py-3 rounded-lg font-semibold border-2 transition-all"
                  style={{
                    borderColor: colors.primary,
                    color: colors.primary,
                    backgroundColor: 'white'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${colors.primary}10`
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white'
                  }}
                >
                  Continuar Buscando
                </button>

                <button
                  onClick={handleProceedToPayment}
                  disabled={submitting || cart.items.length === 0}
                  className="w-full px-4 py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: colors.primary }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      e.currentTarget.style.backgroundColor = colors.primaryDark
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting) {
                      e.currentTarget.style.backgroundColor = colors.primary
                    }
                  }}
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <LoadingSpinner size="sm" />
                      Procesando...
                    </span>
                  ) : (
                    'Proceder al Pago'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default ShoppingCartPage
